/**
 * Generic DataGrid Search History Button
 *
 * A reusable component that provides search history functionality for any DataGrid.
 * It tracks filter changes and allows users to save and restore previous filter states.
 *
 * @example Basic usage with DataGridToolbarDefaultButtons
 * ```tsx
 * import { DataGridToolbarDefaultButtons } from '@cbgms/components/list/DataGridToolbar'
 * import { useSelector } from 'react-redux'
 *
 * function MyGridToolbar() {
 *   const userID = useSelector((state: IRootReducerState) => state.employee?.details?.UUID)
 *   const getUserID = useCallback(() => userID || 'anonymous', [userID])
 *
 *   return (
 *     <DataGridToolbarContainer>
 *       <DataGridToolbarDefaultButtons
 *         historyButton={{
 *           scope: 'my-feature-grid-name',
 *           getUserID
 *         }}
 *       />
 *     </DataGridToolbarContainer>
 *   )
 * }
 * ```
 *
 * @example Standalone usage
 * ```tsx
 * import { DataGridToolbarHistoryButton } from '@cbgms/components/list/DataGridToolbar'
 *
 * function MyCustomToolbar() {
 *   const getUserID = useCallback(() => 'current-user-id', [])
 *
 *   return (
 *     <Stack direction="row">
 *       <DataGridToolbarHistoryButton
 *         scope="customers-list"
 *         getUserID={getUserID}
 *         columnNameTranslator={(key) => t(`myapp.columns.${key}`, key)}
 *       />
 *     </Stack>
 *   )
 * }
 * ```
 *
 * @example Custom column name translation
 * ```tsx
 * const customTranslator = (columnKey: string) => {
 *   const translations = {
 *     'firstName': 'First Name',
 *     'lastName': 'Last Name',
 *     'email': 'Email Address'
 *   }
 *   return translations[columnKey] || columnKey
 * }
 *
 * <DataGridToolbarHistoryButton
 *   scope="users-grid"
 *   getUserID={getUserID}
 *   columnNameTranslator={customTranslator}
 * />
 * ```
 */

import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import { useGridApiContext } from '@mui/x-data-grid-pro'
import { IColumnSearchHistoryItem, useListSearchHistory } from '@cbgms/components/grid/Hooks/use-list-search-history'
import { IColumn, PinnedStatus } from '@cbgms/components/grid/Types'
import { KnockOperator } from '@cbgms/components/grid/Knock/KnockTypes'
import { DataGridToolbarHistoryDialog } from './DataGridToolbarHistoryDialog'

interface DataGridToolbarHistoryButtonProps {
  /**
   * Scope identifier for the search history. Used to separate history between different grids/features.
   * @example 'support-product-warranty-claims', 'main-customers', 'appointment-planner-appointments'
   */
  scope: string

  /**
   * Function to get the current user ID for search history scoping.
   * @example () => useSelector((state: IRootReducerState) => state.employee?.details?.UUID) || 'anonymous'
   */
  getUserID: () => string

  /**
   * Optional custom column name translator function.
   * If not provided, will use default translation key pattern: `entities.${columnKey}`
   */
  columnNameTranslator?: (columnKey: string) => string
}

export function DataGridToolbarHistoryButton({ scope, getUserID, columnNameTranslator }: DataGridToolbarHistoryButtonProps) {
  const { t } = useTranslation()
  const gridApiRef = useGridApiContext()
  const [dialogOpen, setDialogOpen] = React.useState(false)

  // Initialize search history hook
  const { onNewFilterSettings: storeNewSearchHistory, history: searchHistory } = useListSearchHistory({
    scope,
    getUserID
  })

  const handleDialogOpen = () => {
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  // Map current grid columns to IColumn format
  const mappedColumns = useMemo(() => {
    const allColumns = gridApiRef.current.getAllColumns()
    return allColumns.map(
      (column, index): IColumn => ({
        key: column.field,
        name: column.headerName || column.field,
        visible: true,
        index,
        filter: '', // Current filter value
        filterOperator: 'contains' as KnockOperator,
        width: column.width || 200,
        sortOrder: undefined,
        sortIndex: undefined,
        pinned: 'None' as PinnedStatus
      })
    )
  }, [gridApiRef])

  // Default column name translator function
  const defaultColumnNameTranslator = useCallback(
    (columnKey: string) => {
      return t(`entities.${columnKey}`, columnKey)
    },
    [t]
  )

  const translateColumnName = columnNameTranslator || defaultColumnNameTranslator

  // Handle search history item clicks
  const onRecordClick = useCallback(
    (input: IColumnSearchHistoryItem) => {
      const newColumns = mappedColumns.map(a => {
        const newFilterValue = input.filters.find(f => f.Name === a.key) || {
          Name: a.key,
          FilterOperator: a.filterOperator,
          FilterValue: '' // this resets existing filter entries
        }
        return {
          ...a,
          key: newFilterValue.Name,
          filterOperator: newFilterValue.FilterOperator as any as KnockOperator,
          filter: newFilterValue.FilterValue
        }
      })

      // Apply the selected filters to the DataGrid
      const filterItems = newColumns
        .filter(col => col.filter && col.filter.trim() !== '')
        .map((col, index) => ({
          id: index,
          field: col.key,
          operator: col.filterOperator || 'contains',
          value: col.filter
        }))

      // Update the DataGrid filter model
      gridApiRef.current.setFilterModel({
        items: filterItems
      })
    },
    [mappedColumns, gridApiRef]
  )

  // Store new search history when filters change
  const handleFilterModelChange = useCallback(
    (filterModel: any) => {
      // Only store history if filterModel exists and has items with actual values
      if (!filterModel || !filterModel.items || !Array.isArray(filterModel.items)) {
        return
      }

      // Check if there are any meaningful filters (with actual values)
      const hasFilters = filterModel.items.some((item: any) => item && item.value !== undefined && item.value !== null && item.value !== '')

      if (!hasFilters) {
        return // Don't store empty filter states
      }

      // Convert DataGrid filter model to list settings format for history storage
      // Instead of mapping over mappedColumns (which might not include all filtered columns),
      // create entries directly from the filterModel.items
      const listSettingsColumns = filterModel.items.map((filterItem: any, idx: number) => {
        return {
          Name: filterItem.field,
          FilterValue: filterItem.value || '',
          FilterOperator: filterItem.operator || 'contains',
          Index: idx,
          Pinned: 'None', // Default value
          Width: 200, // Default value
          SortDirection: '',
          SortIndex: 0,
          Visible: true
        }
      })

      // Store in search history
      storeNewSearchHistory(listSettingsColumns)
    },
    [mappedColumns, storeNewSearchHistory]
  )

  // Subscribe to filter changes
  React.useEffect(() => {
    const handleFilterChange = (params: any) => {
      // Try different ways to access the filter model
      let filterModel = null

      if (params && params.filterModel) {
        filterModel = params.filterModel
      } else if (params && typeof params === 'object' && params.items) {
        // Sometimes the params itself might be the filterModel
        filterModel = params
      } else {
        // Try to get current filter model directly from the grid API
        try {
          filterModel = gridApiRef.current.state.filter.filterModel
        } catch (error) {
          // Ignore error, filterModel remains null
        }
      }

      if (filterModel) {
        handleFilterModelChange(filterModel)
      }
    }

    const unsubscribe = gridApiRef.current.subscribeEvent('filterModelChange', handleFilterChange)
    return unsubscribe
  }, [gridApiRef, handleFilterModelChange])

  return (
    <>
      <Button variant='text' size='small' startIcon={<HistoryIcon />} onClick={handleDialogOpen} data-testid='list-search-history-button'>
        {t('global.list.search_history', 'Search History')}
      </Button>

      <DataGridToolbarHistoryDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        searchHistory={searchHistory}
        onItemClick={onRecordClick}
        columnNameTranslator={translateColumnName}
      />
    </>
  )
}
